import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import Logo from "./Logo";
import { CONTACT } from "../lib/api";

const links = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/about", label: "About", testId: "nav-about" },
  { to: "/team", label: "Team", testId: "nav-team" },
  { to: "/services", label: "Services", testId: "nav-services" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

const linkBase =
  "text-sm font-medium tracking-wide text-foreground/75 hover:text-foreground transition-colors relative py-2";
const linkActive =
  "text-foreground after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[2px] after:bg-primary after:rounded-full";

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/60"
    >
      <div className="max-w-7xl mx-auto container-px flex items-center justify-between h-24 md:h-28">
        <Logo />

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testId}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={CONTACT.phoneDial}
            data-testid="nav-phone-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground transition"
          >
            <Phone className="h-4 w-4 text-primary" />
            {CONTACT.phone}
          </a>
          <Button
            asChild
            data-testid="nav-cta-contact"
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-5 h-11 text-sm font-medium shadow-sm"
          >
            <Link to="/contact">Enquire</Link>
          </Button>
        </div>

        {/* Mobile */}
        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-trigger"
                className="rounded-full"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background w-[85vw] sm:w-[380px]">
              <SheetHeader>
                <SheetTitle className="font-heading text-2xl text-left">Menu</SheetTitle>
                <SheetDescription className="sr-only">
                  Site navigation and contact options
                </SheetDescription>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-1">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`${l.testId}-mobile`}
                    className={({ isActive }) =>
                      `px-4 py-4 rounded-2xl text-lg font-medium transition ${
                        isActive
                          ? "bg-secondary text-foreground"
                          : "text-foreground/80 hover:bg-secondary/70"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
              <div className="mt-8 border-t border-border pt-6 flex flex-col gap-3">
                <a
                  href={CONTACT.phoneDial}
                  data-testid="mobile-phone-link"
                  className="inline-flex items-center gap-2 text-sm font-medium text-foreground"
                >
                  <Phone className="h-4 w-4 text-primary" />
                  {CONTACT.phone}
                </a>
                <Button
                  asChild
                  data-testid="mobile-cta-contact"
                  className="rounded-full bg-primary text-primary-foreground h-12"
                  onClick={() => setOpen(false)}
                >
                  <Link to="/contact">Enquire now</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
